/**
 * Sentry Breadcrumb Utilities
 * 
 * Centralized helpers for adding breadcrumbs to Sentry tracking.
 * Breadcrumbs provide context for errors and events.
 */

import * as Sentry from "@sentry/react";

/**
 * Add a breadcrumb to Sentry
 * 
 * @param category - Breadcrumb category (e.g., 'iproov-event', 'api', 'user-action')
 * @param message - Human-readable message
 * @param level - Severity level ('debug', 'info', 'warning', 'error')
 * @param data - Additional structured data
 */
export function addBreadcrumb(
  category: string,
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
  });
}

/**
 * Add an iProov event breadcrumb
 * Convenience method for iProov-specific events
 * 
 * @param event - iProov event name (e.g., 'ready', 'started', 'passed')
 * @param verificationId - Current verification ID
 * @param additionalData - Any additional data to include
 */
export function addIProovEventBreadcrumb(
  event: string,
  verificationId: string | null,
  additionalData?: Record<string, any>
): void {
  addBreadcrumb(
    'iproov-event',
    `iProov ${event}`,
    'info',
    {
      verificationId,
      ...additionalData,
    }
  );
}

/**
 * Add an API call breadcrumb
 * 
 * @param endpoint - API endpoint (e.g., 'POST /v1/verifications/faces')
 * @param status - Response status or 'pending'
 * @param additionalData - Additional data
 */
export function addAPIBreadcrumb(
  endpoint: string,
  status: string | number,
  additionalData?: Record<string, any>
): void {
  addBreadcrumb(
    'api',
    `API: ${endpoint}`,
    typeof status === 'number' && status >= 400 ? 'error' : 'info',
    {
      endpoint,
      status,
      ...additionalData,
    }
  );
}

/**
 * Add a user action breadcrumb
 *
 * @param action - User action description
 * @param additionalData - Additional data
 */
export function addUserActionBreadcrumb(
  action: string,
  additionalData?: Record<string, any>
): void {
  addBreadcrumb(
    'user-action',
    action,
    'info',
    additionalData
  );
}

/**
 * Add a verification flow state breadcrumb
 *
 * @param state - Current state (e.g., 'initializing', 'ready', 'processing')
 * @param verificationId - Verification session ID
 * @param additionalData - Additional data (e.g., progress)
 */
export function addVerificationFlowBreadcrumb(
  state: string,
  verificationId: string | null,
  additionalData?: Record<string, any>
): void {
  addBreadcrumb(
    'verification-flow',
    `State transition: ${state}`,
    'info',
    {
      verificationId: verificationId || 'unknown',
      ...additionalData,
    }
  );
}
